import { useState } from 'react'
import { Loader2, Save, ChevronDown, ChevronRight, Clock } from 'lucide-react'
import { useSystemSettings, type SystemSetting } from '@/hooks/useSystemSettings'
import { toast } from '@/hooks/use-toast'

const SETTING_LABELS: Record<string, { label: string; description: string; type: 'number' | 'text' | 'boolean' }> = {
  payment_required: {
    label: 'Payment Required',
    description: 'Require Stripe payment when creating a league',
    type: 'boolean',
  },
  season_registration_fee: {
    label: 'Season Registration Fee (USD)',
    description: 'Amount charged to create a new league',
    type: 'number',
  },
  max_managers_per_league: {
    label: 'Max Managers Per League',
    description: 'Maximum number of members allowed per league',
    type: 'number',
  },
}

export default function AdminPlatformSettings() {
  const { settings, isLoading, updateSetting, getSettingHistory } = useSystemSettings()
  const [editValues, setEditValues] = useState<Record<string, string>>({})
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  function handleEdit(key: string, currentValue: unknown) {
    if (!(key in editValues)) {
      setEditValues(prev => ({ ...prev, [key]: String(currentValue ?? '') }))
    }
  }

  async function handleSave(key: string) {
    const raw = editValues[key]
    if (raw === undefined) return

    const meta = SETTING_LABELS[key]
    const value = meta?.type === 'number' ? Number(raw) : raw

    if (meta?.type === 'number' && (isNaN(value as number) || (value as number) < 0)) {
      toast({ title: 'Invalid value', description: 'Must be a non-negative number', variant: 'destructive' })
      return
    }

    try {
      await updateSetting.mutateAsync({ key, value })
      setEditValues(prev => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      toast({ title: 'Setting updated', description: `${meta?.label ?? key} set to ${value}` })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save'
      toast({ title: 'Save failed', description: msg, variant: 'destructive' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Platform Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Global defaults for the platform. Changes are tracked with full history.</p>
      </div>

      <div className="space-y-3">
        {/* Merge DB settings with any SETTING_LABELS keys not yet in DB */}
        {(() => {
          const dbKeys = new Set(settings.map(s => s.key))
          const missingKeys = Object.keys(SETTING_LABELS).filter(k => !dbKeys.has(k))
          const defaults: Record<string, unknown> = { payment_required: false, season_registration_fee: 20, max_managers_per_league: 20 }
          const missing = missingKeys.map(key => ({ key, value: defaults[key] ?? '', updated_by: null, created_at: new Date().toISOString() }))
          return [...settings, ...missing]
        })().map((setting) => {
          const meta = SETTING_LABELS[setting.key]
          const isEditing = setting.key in editValues
          const history = getSettingHistory(setting.key)
          const isExpanded = expandedKey === setting.key

          return (
            <div
              key={setting.key}
              className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
            >
              <div className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {meta?.label ?? setting.key}
                    </p>
                    {meta?.description && (
                      <p className="text-xs text-zinc-500 mt-0.5">{meta.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {meta?.type === 'boolean' ? (
                      <button
                        onClick={async () => {
                          const current = setting.value === true || setting.value === 'true'
                          try {
                            await updateSetting.mutateAsync({ key: setting.key, value: !current })
                            toast({ title: 'Setting updated', description: `${meta.label} ${!current ? 'enabled' : 'disabled'}` })
                          } catch (e) {
                            const msg = e instanceof Error ? e.message : 'Failed to save'
                            toast({ title: 'Save failed', description: msg, variant: 'destructive' })
                          }
                        }}
                        disabled={updateSetting.isPending}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors
                                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40
                                    disabled:opacity-50
                                    ${(setting.value === true || setting.value === 'true')
                                      ? 'bg-emerald-600'
                                      : 'bg-zinc-200 dark:bg-zinc-700'}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform
                                      ${(setting.value === true || setting.value === 'true') ? 'translate-x-5' : 'translate-x-0'}`}
                        />
                      </button>
                    ) : isEditing ? (
                      <>
                        <input
                          type={meta?.type === 'number' ? 'number' : 'text'}
                          value={editValues[setting.key]}
                          onChange={(e) => setEditValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                          className="w-24 px-2.5 py-1.5 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                                     bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                                     focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                          min={0}
                        />
                        <button
                          onClick={() => handleSave(setting.key)}
                          disabled={updateSetting.isPending}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg
                                     bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {updateSetting.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          Save
                        </button>
                        <button
                          onClick={() => setEditValues(prev => {
                            const next = { ...prev }
                            delete next[setting.key]
                            return next
                          })}
                          className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-mono text-zinc-900 dark:text-zinc-100">
                          {String(setting.value)}
                        </span>
                        <button
                          onClick={() => handleEdit(setting.key, setting.value)}
                          className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <p className="text-xs text-zinc-400">
                    Last updated {new Date(setting.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                  {history.length > 1 && (
                    <button
                      onClick={() => setExpandedKey(isExpanded ? null : setting.key)}
                      className="flex items-center gap-0.5 text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      {history.length} changes
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && history.length > 1 && (
                <div className="border-t border-zinc-100 dark:border-zinc-800 px-5 py-3 space-y-2">
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Change History</p>
                  {history.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs">
                      <Clock className="h-3 w-3 text-zinc-400 shrink-0" />
                      <span className="font-mono text-zinc-700 dark:text-zinc-300">{String(h.value)}</span>
                      <span className="text-zinc-400">
                        {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                      {i === 0 && <span className="text-emerald-600 dark:text-emerald-400 font-medium">current</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
